import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { memberApi, useMemberMutation, errorMessage } from "@/hooks/useMember";

const scale = [1, 2, 3, 4, 5];
const inputCls = "w-full bg-cream border border-forest/20 px-4 py-3 text-sm text-charcoal focus:border-forest outline-none transition-colors";

function Scale({ label, value, onChange, testid }) {
  return (
    <div>
      <p className="text-sm text-charcoal/80">{label}</p>
      <div className="mt-2 flex gap-2" role="radiogroup" aria-label={label}>
        {scale.map((n) => (
          <button key={n} type="button" role="radio" aria-checked={value === n} onClick={() => onChange(n)} data-testid={`${testid}-${n}`}
            className={`w-10 h-10 border text-sm transition-colors ${value === n ? "bg-forest text-ivory border-forest" : "border-forest/20 text-charcoal/70 hover:border-forest"}`}>{n}</button>
        ))}
      </div>
    </div>
  );
}

function CheckinForm({ trial, day, existing }) {
  const blank = { texture: "", comfort: null, routine_fit: null, overall: null, would_continue: null, notes: "" };
  const [f, setF] = useState({ ...blank, ...(existing || {}) });
  useEffect(() => { setF({ ...blank, ...(existing || {}) }); }, [existing, day]); // eslint-disable-line
  const save = useMemberMutation(memberApi.saveCheckin, (_, vars) => toast.success(vars.body.draft ? "Draft saved." : `Day ${day} check-in saved privately.`));
  const locked = trial.status !== "in_progress" && trial.status !== "completed";

  const submit = (draft) => {
    const body = { ...f, draft, comfort: f.comfort || null, routine_fit: f.routine_fit || null, overall: f.overall || null };
    save.mutate({ trialId: trial.trial_id, day, body }, { onError: (e) => toast.error(errorMessage(e)) });
  };

  if (locked) return <p className="mt-6 text-sm text-charcoal/60 font-light">Check-ins open once you confirm your product has arrived.</p>;

  return (
    <div className="mt-6 space-y-6" data-testid={`checkin-form-day-${day}`}>
      <div className="flex items-center justify-between">
        <p className="font-serif text-2xl text-forest">Day {day}</p>
        {existing && <span className="text-[10px] uppercase tracking-[0.22em] text-moss" data-testid="checkin-saved-state">{existing.status === "draft" ? "Draft" : "Saved"} · editable</span>}
      </div>
      <label className="block">
        <span className="text-sm text-charcoal/80">How would you describe the texture and feel?</span>
        <input value={f.texture || ""} onChange={(e) => setF({ ...f, texture: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="checkin-texture" placeholder="In your own words" />
      </label>
      <Scale label="Comfort on your skin" value={f.comfort} onChange={(v) => setF({ ...f, comfort: v })} testid="checkin-comfort" />
      <Scale label="Fit with your everyday routine" value={f.routine_fit} onChange={(v) => setF({ ...f, routine_fit: v })} testid="checkin-routine" />
      <Scale label="Overall experience so far" value={f.overall} onChange={(v) => setF({ ...f, overall: v })} testid="checkin-overall" />
      <div>
        <p className="text-sm text-charcoal/80">Would you keep this in your ritual?</p>
        <div className="mt-2 flex gap-2">
          {[["Yes", true], ["Not sure", null], ["No", false]].map(([l, v]) => (
            <button key={l} type="button" onClick={() => setF({ ...f, would_continue: v })} data-testid={`checkin-continue-${l.toLowerCase().replace(" ", "-")}`}
              className={`px-4 py-2 border text-[11px] uppercase tracking-[0.18em] transition-colors ${f.would_continue === v ? "bg-forest text-ivory border-forest" : "border-forest/20 text-charcoal/70 hover:border-forest"}`}>{l}</button>
          ))}
        </div>
      </div>
      <label className="block">
        <span className="text-sm text-charcoal/80">Anything else — positive or critical</span>
        <textarea rows={4} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="checkin-notes" />
      </label>
      <div className="flex flex-col sm:flex-row gap-3">
        <button type="button" onClick={() => submit(false)} disabled={save.isPending} className="btn btn-primary disabled:opacity-60" data-testid="checkin-save">Save check-in</button>
        <button type="button" onClick={() => submit(true)} disabled={save.isPending} className="btn btn-outline disabled:opacity-60" data-testid="checkin-save-draft">Save as draft</button>
      </div>
    </div>
  );
}

export default function CheckinJournal({ trials }) {
  const active = trials.filter((t) => ["in_progress", "completed", "dispatched", "awaiting_dispatch"].includes(t.status));
  const [trialId, setTrialId] = useState(active[0]?.trial_id);
  const trial = active.find((t) => t.trial_id === trialId) || active[0];
  const [day, setDay] = useState(trial?.checkin_days?.[0] || 1);

  if (!trial) {
    return (
      <div className="border border-forest/20 p-1"><div className="border border-forest/10 px-8 py-14 text-center">
        <Lock size={20} strokeWidth={1.5} className="mx-auto text-moss" />
        <p className="mt-5 font-serif font-light text-3xl text-forest">Your private journal</p>
        <p className="mt-3 text-charcoal/65 max-w-md mx-auto font-light">Once you're in a trial, you'll record private check-ins here on Day 1, 7, 14 and 28 — texture, comfort, routine fit and honest notes. Private by default.</p>
      </div></div>
    );
  }

  const byDay = Object.fromEntries((trial.checkins || []).map((c) => [c.day, c]));

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="checkin-journal">
      <aside className="lg:col-span-4">
        {active.length > 1 && (
          <select value={trial.trial_id} onChange={(e) => setTrialId(e.target.value)} className={`${inputCls} mb-5`} data-testid="journal-trial-select">
            {active.map((t) => <option key={t.trial_id} value={t.trial_id}>{t.programme?.title}</option>)}
          </select>
        )}
        <p className="text-[10px] uppercase tracking-[0.22em] text-champagne">{trial.product?.name}</p>
        <p className="mt-2 font-serif text-2xl text-forest font-light leading-tight">{trial.programme?.title}</p>
        <ol className="mt-6 border-t border-forest/15">
          {trial.checkin_days.map((d) => {
            const c = byDay[d];
            return (
              <li key={d}>
                <button onClick={() => setDay(d)} data-testid={`checkin-day-${d}`} className={`w-full flex items-center justify-between py-4 border-b border-forest/15 text-left transition-colors ${day === d ? "text-forest" : "text-charcoal/55 hover:text-forest"}`}>
                  <span className="font-serif text-xl">Day {d}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em]">{c ? (c.status === "draft" ? "Draft" : "Saved") : "Not started"}</span>
                </button>
              </li>
            );
          })}
        </ol>
        <p className="mt-6 flex items-start gap-2 text-xs text-charcoal/50 font-light"><Lock size={13} className="mt-0.5 shrink-0" /> Check-ins are private to you and ELA. Nothing here is published.</p>
      </aside>
      <div className="lg:col-span-8 border border-forest/15 bg-cream p-6 sm:p-8">
        <CheckinForm trial={trial} day={day} existing={byDay[day]} />
      </div>
    </div>
  );
}
